import type { RecapBeat } from '@spirits/shared';

interface Props {
  title: string;
  blurb: string;
  beats: RecapBeat[];
  closing: string;
  storyTitle?: string;
  endingTitle?: string;
  category?: string;
}

// Renders a structured recap — the run's title + hook, one narration per beat
// (with a divergence card where the player's path bent away from the original),
// and a closing. Shared by the in-game finish overlay and the public /s/ page.
export function RecapView({ title, blurb, beats, closing, storyTitle, endingTitle, category }: Props) {
  return (
    <article className="space-y-6">
      <header className="space-y-2 text-center">
        {storyTitle && (
          <p className="text-[11px] uppercase tracking-[0.25em] text-amber-300/70">
            {category ? `${category} · ` : ''}{storyTitle}
          </p>
        )}
        <h1 className="text-3xl sm:text-4xl text-slate-100 leading-tight">{title}</h1>
        {blurb && <p className="text-slate-400 italic leading-snug max-w-2xl mx-auto">{blurb}</p>}
        {endingTitle && (
          <p className="inline-block mt-1 rounded-full border border-amber-400/40 bg-amber-950/30 px-3 py-1 text-xs text-amber-200/90">
            ending · {endingTitle}
          </p>
        )}
      </header>

      <div className="space-y-5">
        {beats.map((b, i) => (
          <section key={i} className="space-y-2">
            <div className="flex items-center gap-3">
              <span className="text-[10px] uppercase tracking-[0.2em] text-slate-500 whitespace-nowrap">
                ✦ {b.sceneName}
              </span>
              <span className="h-px flex-1 bg-slate-800" />
            </div>
            <p className="text-[15px] leading-relaxed text-slate-200 whitespace-pre-line">{b.narration}</p>
            {b.divergence && (
              <div className="rounded-xl border border-violet-500/30 bg-violet-950/20 px-4 py-3 text-sm space-y-1.5">
                <p className="text-[10px] uppercase tracking-widest text-violet-300/80">🔀 a road not taken</p>
                <p className="text-violet-200/80 leading-snug">
                  <span className="text-violet-400/70">In the original: </span>{b.divergence.canon}
                </p>
                <p className="text-slate-300 leading-snug">
                  <span className="text-amber-300/70">Your story: </span>{b.divergence.mine}
                </p>
              </div>
            )}
          </section>
        ))}
      </div>

      {closing && (
        <footer className="pt-2 border-t border-slate-800">
          <p className="text-[15px] leading-relaxed text-slate-200 whitespace-pre-line">{closing}</p>
        </footer>
      )}
    </article>
  );
}
